/**
 * The "Kom igång" checklist for a new company account (P25009-385 BR-5).
 *
 * "A new company sees a Get started checklist (for example verify payout, add
 * address, create services, first listing, enable alerts) that dismisses itself
 * when complete."
 *
 * Every step here is tied to a signal the app can actually read, so a step can
 * always be completed. The ticket's fifth example, "enable alerts", is
 * deliberately **not** included: there is no seller notification setting in this
 * codebase to switch on, so the step could never be ticked. A checklist with a
 * permanently unfinished row never dismisses itself, which would break BR-5 and
 * quietly tell every seller they are not done. Add the step when the setting
 * exists.
 *
 * Like the action centre, this is pure and derives state rather than storing it,
 * so "dismisses itself when complete" needs no persistence and cannot drift out
 * of sync with reality.
 */

/**
 * @param {object} input
 * @param {boolean}      input.stripeReady        account connected and nothing outstanding
 * @param {number}       input.addressCount       saved pickup/return addresses
 * @param {number}       input.serviceCount       profile.publicData.services length
 * @param {boolean}      input.hasPublishedListing
 * @param {boolean}      input.hasShopProfile     display name and description filled in
 * @param {boolean}      input.canConnectStock    a business account that may upload files
 * @param {boolean}      input.hasCatalogue       has catalogue rows (P25009-410)
 */
export const buildOnboardingSteps = (input = {}) => {
  const {
    stripeReady = false,
    addressCount = 0,
    serviceCount = 0,
    hasPublishedListing = false,
    hasShopProfile = false,
    canConnectStock = false,
    hasCatalogue = false,
  } = input;

  /* P25009-410 BR-1 — a business account starts the same way whatever it is:
   * "Anslut ditt lager". A full catalogue with no ads at all ticks the step
   * (BR-3 — "Inget just nu" is a complete answer), so it is done by either.
   * Accounts that cannot upload a file keep "first listing": sending them to
   * a door that only says "kontakta oss" would be a step they can never tick.
   * `search` opens step 1 straight away on arrival. */
  const stockStep = canConnectStock
    ? {
        id: 'stock',
        done: !!hasPublishedListing || !!hasCatalogue,
        labelKey: 'SellerDashboardPage.onboarding.stock',
        route: 'WheelplaceBulkUploadPage',
        search: '?anslut=1',
      }
    : {
        id: 'listing',
        done: !!hasPublishedListing,
        labelKey: 'SellerDashboardPage.onboarding.listing',
        route: 'WheelplaceCreateListingPage',
      };

  /* Order follows what actually unblocks a sale: you cannot be paid without a
   * payout account, cannot ship without an address, and nothing else matters
   * until something is published. Services and the shop profile improve a
   * listing but do not gate it, so they come last. */
  return [
    {
      id: 'payout',
      done: !!stripeReady,
      labelKey: 'SellerDashboardPage.onboarding.payout',
      route: 'StripePayoutPage',
    },
    {
      id: 'address',
      done: addressCount > 0,
      labelKey: 'SellerDashboardPage.onboarding.address',
      route: 'ManageWarehousePage',
    },
    stockStep,
    {
      id: 'services',
      done: serviceCount > 0,
      labelKey: 'SellerDashboardPage.onboarding.services',
      route: 'ManageServicesPage',
    },
    {
      id: 'profile',
      done: !!hasShopProfile,
      labelKey: 'SellerDashboardPage.onboarding.profile',
      route: 'ProfileSettingsPage',
    },
  ];
};

/** How many steps are done, for the "2 av 5 klara" heading. */
export const completedCount = steps => (steps || []).filter(s => s.done).length;

/** The first unfinished step — what the card's single button points at. */
export const nextStep = steps => (steps || []).find(s => !s.done) || null;

/**
 * Should the checklist be shown at all?
 *
 * Hidden once every step is done (BR-5: "dismisses itself when complete").
 *
 * Also hidden for sellers who are clearly already running: a business that has
 * published listings and can take payouts does not need a Get started card just
 * because it never filled in a service list. The card is for the first hour of
 * an account's life, not a permanent scorecard.
 */
export const showOnboarding = (steps, { hasPublishedListing = false, stripeReady = false } = {}) => {
  const list = steps || [];
  if (list.length === 0) return false;
  if (completedCount(list) === list.length) return false;
  if (hasPublishedListing && stripeReady) return false;
  return true;
};

/**
 * May this account take the "Anslut ditt lager" route (P25009-410)?
 *
 * A business account, and the same file-upload entitlement the server checks
 * in api/bulk-listing-upload.js: Enterprise, or bulk upload switched on.
 */
export const canConnectStockFor = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  const metadata = profile.metadata || {};
  return (
    profile.publicData?.userType === 'business' &&
    (!!metadata.bulkUpload || metadata.subscriptionName === 'enterprise')
  );
};

/**
 * Does this user have a shop profile worth calling complete?
 *
 * A display name alone is not enough — Sharetribe fills one in at signup, so
 * every account would start with this step already ticked. Requiring a
 * description means the seller has actually written something.
 */
export const hasShopProfileFrom = currentUser => {
  const profile = currentUser?.attributes?.profile;
  const displayName = (profile?.displayName || '').trim();
  const bio = (profile?.bio || '').trim();
  const publicDescription = (profile?.publicData?.storeDescription || '').trim();
  return !!displayName && (bio.length > 0 || publicDescription.length > 0);
};
